
import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, MessageCircle, Target, Globe, Zap, Sparkles, TrendingUp, Send } from 'lucide-react';
import { Language } from '../types';

interface WorkshopsPageProps {
  lang: Language;
  onBack: () => void;
}

const WorkshopsPage: React.FC<WorkshopsPageProps> = ({ lang, onBack }) => {
  const gradientTextClasses = "bg-gradient-to-r from-primary via-teal-400 to-emerald-500 bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]";
  const gradientBgClasses = "bg-gradient-to-r from-primary via-teal-400 to-emerald-500 animate-gradient bg-[length:200%_auto]";

  const workshopList = [
    { 
      id: 'nvc',
      icon: <MessageCircle className="w-8 h-8" />,
      title: { en: "NVC Workshop", fa: "کارگاه ارتباط بدون خشونت" },
      desc: { en: "Master empathy and transform conflicts.", fa: "مهارت در همدلی و تبدیل دعوا به دوستی." }
    },
    { 
      id: 'goals',
      icon: <Target className="w-8 h-8" />,
      title: { en: "Big Goal Setting", fa: "کارگاه هدف‌های بزرگ" },
      desc: { en: "Designing a life with purpose and meaning.", fa: "طراحی زندگی با هدف و معنای عمیق." }
    },
    { 
      id: 'travel',
      icon: <Globe className="w-8 h-8" />,
      title: { en: "World Travel Roadmap", fa: "کارگاه شروع سفر به دور دنیا" },
      desc: { en: "The mindset and tools for the long road.", fa: "ذهنیت و ابزارهای لازم برای جاده‌های طولانی." }
    },
    { 
      id: 'digital',
      icon: <Zap className="w-8 h-8" />,
      title: { en: "Digital Tools for Coaches", fa: "ابزارهای دیجیتال برای کوچ‌ها" },
      desc: { en: "Scale your impact with the modern stack.", fa: "افزایش تأثیرگذاری با ابزارهای مدرن." }
    },
    { 
      id: 'fitness',
      icon: <Sparkles className="w-8 h-8" />,
      title: { en: "Mental Fitness", fa: "کارگاه آمادگی ذهنی" },
      desc: { en: "Resilience through presence and awareness.", fa: "قوی‌تر شدن از طریق حضور و آگاهی." }
    },
    { 
      id: 'growth',
      icon: <TrendingUp className="w-8 h-8" />,
      title: { en: "Business Growth for Coaches", fa: "رشد کسب‌وکار برای کوچ‌ها" },
      desc: { en: "Building a sustainable and soulful practice.", fa: "ساختن یه کار پایدار و باحال." }
    }
  ];

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <button 
            onClick={onBack}
            className={`flex items-center gap-2 ${gradientTextClasses} font-black uppercase text-xs tracking-widest hover:translate-x-[-4px] transition-transform mb-8`}
          >
            <ArrowLeft size={16} className="text-teal-500" />
            {lang === 'en' ? "Back to Home" : "بازگشت به خانه"}
          </button>
          
          <div className="flex flex-col md:flex-row justify-between items-end gap-6">
            <h1 className="hero-heading font-black text-brandDark uppercase leading-none">
              {lang === 'en' ? <>Specialized<br/><span className={`${gradientTextClasses} italic`}>Workshops.</span></> : <>کارگاه‌های<br/><span className={`${gradientTextClasses} italic`}>تخصصی.</span></>}
            </h1>
            <p className="text-gray-500 text-xl font-bold uppercase tracking-widest md:max-w-lg text-right rtl:text-left leading-tight">
              {lang === 'en' 
                ? "Join the next cohort. Deep dives into practical wisdom." 
                : "به گروه بعدی بیا. غواصی عمیق تو خرد کاربردی."}
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {workshopList.map((workshop, idx) => (
            <motion.div 
              key={workshop.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              whileHover={{ y: -5 }}
              className="bg-white p-8 md:p-10 border border-gray-100 rounded-sm shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col h-full group"
            >
              <div className="text-teal-500 mb-6 group-hover:scale-110 transition-transform">{workshop.icon}</div>
              <h3 className="text-2xl font-black text-brandDark uppercase mb-2 leading-tight">
                {workshop.title[lang]}
              </h3>
              <p className="text-gray-500 font-medium flex-grow">
                {workshop.desc[lang]}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 flex flex-col items-center justify-center text-center space-y-8 p-12 bg-gray-50 rounded-sm border border-gray-100"
        >
          <h3 className="text-2xl md:text-3xl font-black uppercase text-brandDark">
            {lang === 'en' ? "Ready to dive deeper?" : "آماده‌ای عمیق‌تر شی؟"}
          </h3>
          <p className="max-w-xl text-gray-500 font-medium">
             {lang === 'en' 
               ? "Spaces are limited to ensure intimacy and impact. Send a request to join the waitlist." 
               : "ظرفیت محدوده تا بتونیم عمیق کار کنیم. اگه می‌خوای باشی، درخواست بده."}
          </p>
          <a 
            href="mailto:kayvan.coaching@gmail.com?subject=Workshop Inquiry"
            className="inline-flex items-center gap-3 bg-brandDark text-white px-10 py-5 text-lg font-black uppercase tracking-widest hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-teal-200 hover:via-white hover:to-teal-200 transition-all rounded-full shadow-lg hover:shadow-xl group"
          >
            <Send className="w-5 h-5 text-teal-400 group-hover:text-white" />
            {lang === 'en' ? "Inquire Here" : "درخواست اطلاعات"}
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default WorkshopsPage;
